// Staff copy for the counter, in the three languages a branch in Azerbaijan works in.
// Native review pending (az, ru): the Azerbaijani and Russian here are a first
// draft. Change a phrase in all three languages at once, never in one.
// Few words (ui-lab/DESIGN.md, "Words"): labels of one to three words, no
// explanation paragraphs; a confirmation of something destructive is one line.
import type { Phrase } from '../staffText'

// English only for now (redesign): new phrases leave az and ru empty.
const en = (text: string): Phrase => ({ en: text, az: '', ru: '' })

export const counter = {
  // What went wrong. {what} is one of the busy words below.
  'counter.nobodyWaiting': { en: 'Nobody waiting', az: 'Heç kim gözləmir', ru: 'Никто не ждёт' },
  'counter.notCallableYet': en('Nobody callable yet'),
  'counter.failed': { en: '{what} failed: {error}', az: '{what} alınmadı: {error}', ru: '{what}: не удалось — {error}' },

  // The busy words, passed to run(); each names the act while it is in flight.
  'counter.busy.transferring': { en: 'Transferring', az: 'Yönləndirilir', ru: 'Перевод' },
  'counter.busy.closing': { en: 'Closing', az: 'Bağlanır', ru: 'Завершение' },
  'counter.busy.adding': { en: 'Adding to the visit', az: 'Müraciətə əlavə edilir', ru: 'Добавление к обращению' },
  'counter.busy.clearing': { en: 'Clearing the visit', az: 'Müraciət təmizlənir', ru: 'Очистка обращения' },
  'counter.busy.recordingDelivered': { en: 'Recording what was delivered', az: 'Göstərilən xidmət qeyd olunur', ru: 'Отметка оказанного' },
  'counter.busy.takingBack': { en: 'Taking it back', az: 'Geri götürülür', ru: 'Отмена' },
  'counter.busy.calling': { en: 'Calling', az: 'Çağırılır', ru: 'Вызов' },
  'counter.busy.stopping': { en: 'Stopping', az: 'Dayandırılır', ru: 'Остановка' },
  'counter.busy.waiting': { en: 'Waiting', az: 'Gözlənilir', ru: 'Ожидание' },
  'counter.busy.serving': { en: 'Serving', az: 'Xidmət başlayır', ru: 'Начало обслуживания' },
  'counter.busy.recalling': { en: 'Recalling', az: 'Təkrar çağırılır', ru: 'Повторный вызов' },
  'counter.busy.markingNoShow': { en: 'Marking no-show', az: '«Gəlmədi» qeyd olunur', ru: 'Отметка «Не явился»' },
  'counter.busy.parking': { en: 'Holding', az: '', ru: '' },
  'counter.busy.opening': { en: 'Opening the desk', az: 'Pəncərə açılır', ru: 'Открытие окна' },
  'counter.busy.walkIn': { en: 'Serving the walk-in', az: 'Talonsuz müştəriyə xidmət', ru: 'Обслуживание без талона' },
  'counter.busy.sending': { en: 'Sending', az: 'Göndərilir', ru: 'Отправка' },
  'counter.busy.puttingBackInLine': { en: 'Putting back in line', az: 'Növbəyə qaytarılır', ru: 'Возврат в очередь' },
  'counter.busy.closingDesk': { en: 'Closing the counter', az: '', ru: '' },
  'counter.busy.resuming': { en: 'Resuming', az: 'Davam edilir', ru: 'Возобновление' },
  'counter.busy.asking': { en: 'Asking', az: 'Sorğu göndərilir', ru: 'Отправка запроса' },
  'counter.busy.cancelling': { en: 'Cancelling', az: 'Ləğv edilir', ru: 'Отмена запроса' },
  'counter.busy.droppingReturn': { en: 'Not sending back', az: 'Qaytarılma ləğv edilir', ru: 'Отмена возврата' },

  // The verbs.
  'counter.callNext': { en: 'Call next', az: 'Növbətini çağır', ru: 'Вызвать следующего' },
  'counter.stopWaiting': { en: 'Stop waiting', az: 'Gözləməni dayandır', ru: 'Перестать ждать' },
  'counter.waitForNext': { en: 'Wait for next', az: 'Növbətini gözlə', ru: 'Ждать следующего' },
  'counter.startServing': { en: 'Start serving', az: 'Xidmətə başla', ru: 'Начать обслуживание' },
  'counter.recall': { en: 'Recall', az: 'Təkrar çağır', ru: 'Вызвать повторно' },
  'counter.noShow': { en: 'No-show', az: 'Gəlmədi', ru: 'Не явился' },
  'counter.transfer': { en: 'Transfer', az: 'Yönləndir', ru: 'Перевести' },
  'counter.park': { en: 'Hold', az: '', ru: '' },
  'counter.addService': { en: 'Add service', az: 'Xidmət əlavə et', ru: 'Добавить услугу' },

  // Hand-off and back (the organisation may name the button: handoffLabel).
  'counter.toCashier': { en: 'Hand off', az: '', ru: '' },
  'counter.comeBack': { en: 'Come back to me', az: 'Mənə qayıtsın', ru: 'Вернуть ко мне' },
  'counter.finishAtCashier': { en: 'Finish there', az: '', ru: '' },
  'counter.comingBack': { en: 'Coming back', az: '', ru: '' },
  'counter.returnsTo': { en: 'Returns to {name}', az: 'Qayıdır: {name}', ru: 'Вернётся: {name}' },
  'counter.returnsBack': { en: 'Returns', az: 'Qayıdır', ru: 'Вернётся' },
  'counter.dontSendBack': { en: "Don't send back", az: 'Geri göndərmə', ru: 'Не возвращать' },
  'counter.returned': { en: 'Back', az: 'Qayıdıb', ru: 'Вернулся' },
  'counter.away.waiting': { en: 'Waiting', az: 'Gözləyir', ru: 'Ждёт' },
  'counter.away.serving': { en: 'Being served', az: 'Xidmətdə', ru: 'Обслуживается' },
  'counter.closingSendsToQueue': { en: '{n} waiting for you will go to the queue.', az: 'Sizi gözləyən {n} müştəri növbəyə keçəcək.', ru: 'Ждут вас: {n}. Перейдут в очередь.' },
  'counter.takeBreak': { en: 'Break', az: 'Fasiləyə çıx', ru: 'Перерыв' },
  'counter.backFromBreak': { en: 'End break', az: '', ru: '' },
  'counter.back': { en: 'Back', az: 'Geri', ru: 'Назад' },

  // Opening a desk.
  'counter.loadingBranch': { en: 'Loading the branch…', az: 'Filial yüklənir…', ru: 'Загрузка отделения…' },
  'counter.openDesk': { en: 'Open a desk', az: 'Pəncərəni aç', ru: 'Открыть окно' },
  'counter.openNamed': { en: 'Open {desk}', az: 'Aç: {desk}', ru: 'Открыть: {desk}' },
  'counter.desk': { en: 'Desk', az: 'Pəncərə', ru: 'Окно' },
  'counter.deskFree': { en: 'Free', az: 'Boş', ru: 'Свободно' },
  'counter.inUse': { en: 'in use', az: 'məşğuldur', ru: 'занято' },
  'counter.cannotTakeOver': { en: 'Taken? A supervisor frees it on the Live dashboard.', az: 'Məşğuldur? Nəzarətçi onu «Zal» ekranında boşalda bilər.', ru: 'Занято? Супервайзер освободит его на экране «Зал».' },
  'counter.workProfile': { en: 'Work profile', az: 'İş profili', ru: 'Рабочий профиль' },

  // The strip.
  'counter.closeDesk': { en: 'Close counter', az: '', ru: '' },
  'counter.closingPutsBack': { en: '{number} goes back in line to be called again.', az: '{number} yenidən çağırılmaq üçün növbəyə qayıdır.', ru: '{number} вернётся в очередь и будет вызван снова.' },
  'counter.closingParks': { en: '{number} is set aside until a supervisor puts them back.', az: '{number} nəzarətçi qaytarana qədər kənara qoyulur.', ru: '{number} будет отложен, пока супервайзер его не вернёт.' },

  // The stage.
  'counter.atYourDesk': { en: 'At your desk', az: 'Pəncərənizdə', ru: 'У вашего окна' },
  'counter.free': { en: 'Free', az: 'Boş', ru: 'Свободно' },
  'counter.waitingForNext': { en: 'Waiting for next', az: 'Növbəti gözlənilir', ru: 'Ждём следующего' },
  'counter.nWaiting': { en: '{n} waiting', az: '{n} nəfər gözləyir', ru: '{n} ждёт|{n} ждут|{n} ждут' },
  'counter.nextIs': { en: 'Next', az: 'Növbəti', ru: 'Следующий' },
  'counter.onBreak': { en: 'On a break', az: 'Fasilədə', ru: 'На перерыве' },
  'counter.breakEarlier': { en: 'Break (earlier)', az: 'Fasilə (əvvəlki)', ru: 'Перерыв (прежний)' },
  'counter.breakLength': { en: 'Break length', az: 'Fasilənin müddəti', ru: 'Длительность перерыва' },
  'counter.minutes': { en: '{n} min', az: '{n} dəq', ru: '{n} мин' },

  // Desk requests: Pending, Lunch, Short break, Management task, Close — the
  // Ask menu, the status line, and Declined.
  'counter.kind.lunch': { en: 'Lunch', az: 'Nahar', ru: 'Обед' },
  'counter.kind.short_break': { en: 'Short break', az: 'Qısa fasilə', ru: 'Короткий перерыв' },
  'counter.kind.task': { en: 'Task', az: 'Tapşırıq', ru: 'Задача' },
  'counter.pending': { en: 'Pending', az: 'Gözləmədə', ru: 'На согласовании' },
  'counter.pendingWhat': { en: '{kind} · {time}', az: '{kind} · {time}', ru: '{kind} · {time}' },
  // Russian reads this off "Запрос" (the request), not off the kind's own
  // grammatical gender, which "{kind} отклонён/отклонена/отклонено" would have
  // to pick one of — "Запрос" is always masculine, so "отклонён" never disagrees
  // with the kind it names.
  'counter.declinedNoReason': { en: '{kind} declined', az: '{kind} rədd edildi', ru: 'Запрос «{kind}» отклонён' },
  'counter.declinedWithReason': { en: '{kind} declined: {reason}', az: '{kind} rədd edildi: {reason}', ru: 'Запрос «{kind}» отклонён: {reason}' },
  'counter.noBreakReasons': en('No break reasons set'),
  // The customer card and its two dialogs (owner's counter design, 29 Oct).
  'counter.forYou': en('{n} waiting for you'),
  'counter.yourCounter': en('Your counter'),
  'counter.pill.open': en('Desk open'),
  'counter.pill.onBreak': en('On break'),
  'counter.whoWaited': en('{name} · waited {time}'),
  'counter.target': en('target {time}'),
  'counter.viaQueue': en('Queue: {queue}'),
  'counter.fact.service': en('Service'),
  'counter.fact.waited': en('Waited'),
  'counter.fact.onHold': en('On hold'),
  'counter.fact.taken': en('Ticket taken'),
  'counter.fact.via': en('Came via'),
  'counter.fact.language': en('Language'),
  'counter.fact.customer': en('Customer'),
  'counter.noteForColleagues': en('Note for colleagues'),
  'counter.backToQueue': en('Back to queue'),
  'counter.busy.backToQueue': en('Putting back'),
  'counter.customerAdd': en('Add customer'),
  'counter.customerEdit': en('Edit'),
  'counter.customerTitle': en('Customer of {number}'),
  'counter.customerFind': en('Find a customer'),
  'counter.customerFindHint': en('Name or mobile'),
  'counter.customerNone': en('No one found'),
  'counter.customerName': en('Name'),
  'counter.customerPhone': en('Mobile'),
  'counter.customerPhoneHint': en('+994 50 123 45 67'),
  'counter.noteForColleaguesHint': en('Visible if you hold or transfer this customer'),
  'counter.end': en('End'),
  'counter.transferNumber': en('Transfer {number}'),
  'counter.toQueue': en('To a queue'),
  'counter.toCounter': en('To a counter'),
  'counter.toQueueHint': en('They join that queue. Any desk serving it can call them.'),
  'counter.toCounterHint': en('Only that desk or person can call them.'),
  'counter.chooseCounter': en('Choose a counter'),
  'counter.bringBack': en('Send back to me'),
  'counter.bringBackHint': en('{number} returns here when they finish.'),
  'counter.transferred': en('Transferred'),
  'counter.transferredToMe': en('Transferred to me'),
  'counter.noneTransferred': en('Nobody sent to you'),
  'counter.transferredFrom': en('Transferred from {by}'),
  'counter.noteNext': en('Note for the next desk'),
  'counter.optional': en('optional'),
  'counter.noteNextHint': en('For example: needs to pay 250 AZN'),
  'counter.chooseWhere': en('Choose where to send them'),
  'counter.called': { en: 'Called', az: 'Çağırılıb', ru: 'Вызван' },
  'counter.serving': { en: 'Serving', az: 'Xidmət göstərilir', ru: 'Обслуживание' },
  'counter.recalled': { en: ', recalled {n}×', az: ', təkrar çağırılıb: {n}×', ru: ', повторных вызовов: {n}' },
  'counter.waitedFor': { en: 'Waited {time}', az: 'Gözləyib: {time}', ru: 'Ожидание: {time}' },
  'counter.overTarget': { en: ', over {time}', az: ', {time} həddini keçib', ru: ', больше нормы {time}' },
  'counter.overLimit': { en: 'over the {time} limit', az: '{time} həddi keçib', ru: 'дольше лимита {time}' },
  'counter.fact.cameFor': { en: 'Came for', az: 'Gəldiyi xidmət', ru: 'Услуга' },
  'counter.fact.now': { en: 'Now', az: 'İndi', ru: 'Сейчас' },
  'counter.fact.from': { en: 'From', az: 'Haradan', ru: 'Откуда' },
  'counter.fact.name': { en: 'Name', az: 'Ad', ru: 'Имя' },
  'counter.source.kiosk': { en: 'Kiosk', az: 'Kiosk', ru: 'Киоск' },
  'counter.source.reception': { en: 'Reception', az: 'Qəbul', ru: 'Ресепшн' },
  'counter.source.counter': { en: 'Desk', az: 'Pəncərə', ru: 'Окно' },
  'counter.source.mobile': { en: 'Mobile app', az: 'Mobil tətbiq', ru: 'Приложение' },
  'counter.source.appointment': { en: 'Appointment', az: 'Bron', ru: 'Запись' },
  'counter.stillToCome': { en: 'Still to come', az: 'Hələ qarşıda', ru: 'Ещё впереди' },
  'counter.dropRest': { en: 'Drop the rest?', az: 'Qalanı ləğv edilsin?', ru: 'Отменить остальное?' },
  'counter.justThisOne': { en: 'Just this one', az: 'Yalnız bu', ru: 'Только эта' },
  'counter.whatWasDone': { en: 'What was done?', az: 'Nə edildi?', ru: 'Что было сделано?' },

  // Setting aside.
  'counter.parkNumber': { en: 'Hold {number}', az: '', ru: '' },
  'counter.reason': { en: 'Reason', az: 'Səbəb', ru: 'Причина' },
  'counter.reasonOptional': { en: 'Reason (optional)', az: 'Səbəb (istəyə görə)', ru: 'Причина (необязательно)' },
  'counter.backToMe': { en: 'Back to me', az: 'Mənə qayıtsın', ru: 'Вернуть ко мне' },
  'counter.deskOnly': { en: '{desk} only', az: 'Yalnız {desk}', ru: 'Только {desk}' },
  'counter.anyDesk': { en: 'Any desk', az: 'İstənilən pəncərə', ru: 'Любое окно' },

  // Transfer.
  'counter.sendNumber': { en: 'Send {number} to', az: '{number} nömrəsini göndər', ru: 'Направить {number}' },
  'counter.sendToName': { en: 'Send to {name}', az: 'Göndər: {name}', ru: 'Направить: {name}' },
  'counter.send': { en: 'Send', az: 'Göndər', ru: 'Направить' },
  'counter.whereTheyLand': { en: 'Place in line', az: 'Növbədəki yeri', ru: 'Место в очереди' },
  'counter.minutesToHold': { en: 'Minutes to hold them', az: 'Neçə dəqiqə saxlanılsın', ru: 'На сколько минут отложить' },
  'counter.alsoNeeds': { en: 'Also needs', az: 'Həmçinin lazımdır', ru: 'Также нужно' },
  'counter.chooseService': { en: 'Choose a service', az: 'Xidməti seçin', ru: 'Выберите услугу' },

  // Walk-ins and quick serves.
  'counter.serveWalkIn': { en: 'Walk-in', az: 'Talonsuz', ru: 'Без талона' },
  'counter.serviceTheyCameFor': { en: 'The service they came for', az: 'Gəldiyi xidmət', ru: 'Услуга, за которой пришёл клиент' },
  'counter.serve': { en: 'Serve', az: 'Xidmət et', ru: 'Обслужить' },

  // What was delivered.
  'counter.delivered': { en: 'Delivered', az: 'Göstərilən', ru: 'Оказано' },
  'counter.howDidItEnd': { en: 'How did {name} end?', az: '«{name}» necə bitdi?', ru: 'Чем закончилось: «{name}»?' },
  'counter.findDelivered': { en: 'Find a delivered service', az: 'Göstərilən xidməti tap', ru: 'Найти оказанную услугу' },
  'counter.addDelivered': { en: 'Add what was delivered…', az: 'Göstərilən xidməti əlavə et…', ru: 'Добавить оказанное…' },
  'counter.nothingMatches': { en: 'Nothing matches.', az: 'Uyğun heç nə yoxdur.', ru: 'Ничего не найдено.' },

  // The line for this desk.
  'counter.waitingForYou': { en: 'Waiting for you', az: 'Sizi gözləyənlər', ru: 'Ждут вас' },
  'counter.nLongest': { en: '{n}, longest {time}', az: '{n}, ən uzunu {time}', ru: '{n}, дольше всех {time}' },
  'counter.pastTarget': { en: ', {n} past target', az: ', {n} həddi keçib', ru: ', {n} сверх нормы' },
  'counter.next': { en: 'Next', az: 'Növbəti', ru: 'Следующий' },
  'counter.call': { en: 'Call', az: 'Çağır', ru: 'Вызвать' },
  'counter.callNumber': { en: 'Call {number}', az: '{number} nömrəsini çağır', ru: 'Вызвать {number}' },
  'counter.outOfTurn': { en: 'Out of turn. Why now?', az: 'Növbədən kənar. Niyə indi?', ru: 'Вне очереди. Почему сейчас?' },
  'counter.whyNow': { en: 'e.g. missed earlier, elderly customer', az: 'məs. əvvəl buraxılıb, yaşlı müştəri', ru: 'напр. пропустил ранее, пожилой клиент' },
  'counter.heldBack': { en: 'Held back: {list}', az: 'Saxlanılanlar: {list}', ru: 'Отложены: {list}' },
  'counter.heldBackItem': { en: '{number} in {m} min', az: '{number} — {m} dəq sonra', ru: '{number} через {m} мин' },

  // Set aside.
  'counter.setAside': { en: 'On hold', az: '', ru: '' },
  'counter.putBack': { en: 'Put back', az: 'Qaytar', ru: 'Вернуть' },
  'counter.backInMin': { en: 'back in {m} min', az: '{m} dəq sonra qayıdır', ru: 'вернётся через {m} мин' },
  'counter.yours': { en: 'Yours', az: 'Sizinki', ru: 'Ваш' },

  // pages/Counter.tsx's effect that watches the desk under the clerk.
  'counter.signedOutElsewhere': { en: 'You were signed out of {desk} from another screen — by a supervisor, or by the end of the day.', az: '{desk} pəncərəsindən başqa ekrandan çıxarıldınız — nəzarətçi tərəfindən və ya gün bitdiyi üçün.', ru: 'Вас вывели из окна {desk} с другого экрана — супервайзером или в конце дня.' },
  // The redesigned counter: the desk-open wizard, the toolbar, Queue information and the dialogs.
  'counter.wizard.title': en('Open your counter'),
  'counter.wizard.step': en('Step {n} of 2'),
  'counter.wizard.chooseDesk': en('Choose a desk'),
  'counter.wizard.chooseProfile': en('Choose a calling profile'),
  'counter.wizard.nAvailable': en('{n} available'),
  'counter.wizard.available': en('Available'),
  'counter.wizard.allDesks': en('All desks'),
  'counter.wizard.inUse': en('In use'),
  'counter.wizard.change': en('Change'),
  'counter.wizard.search': en('Search'),
  'counter.wizard.noProfiles': en('No calling profile set up'),
  'counter.wizard.noDesks': en('No free desk'),
  'counter.wizard.open': en('Open counter'),
  'counter.queues': en('Queues'),
  'counter.callingProfile': en('Calling profile'),
  'counter.change': en('Change'),
  'counter.changeWork': en('Change profile'),
  'counter.autoCall': en('Auto-call next'),
  'counter.status.ready': en('Ready'),
  'counter.readyToServe': en('Ready to serve'),
  'counter.ticket': en('Ticket'),
  'counter.serviceTime': en('Service time'),
  'counter.waited': en('Waited {time}'),
  'counter.queueInfo': en('Queue information'),
  'counter.stat.waiting': en('Waiting'),
  'counter.stat.longest': en('Longest'),
  'counter.stat.pastTarget': en('Past target'),
  'counter.callAgain': en('Call again'),
  'counter.noneOnHold': en('Nobody on hold'),
  'counter.oot.title': en('Call out of turn?'),
  'counter.oot.notNext': en('{number} is not next in line.'),
  'counter.closeQuestion': en('Close counter?'),
  'counter.closingEmpty': en('{desk} will show as closed.'),
  'counter.fact.priority': en('Priority'),
  'counter.cancelRequest': en('Cancel request'),
  'counter.pendingChange': en('Next: {name}'),
  'counter.pendingTitle': en('Changes when this visit ends'),
} satisfies Record<`counter.${string}`, Phrase>
